import React, { createContext, useContext, useState, useCallback } from 'react';
import { toInitCaps } from '../utils/nameFormat';
import * as readApi from '../api/readApi';

const PlayerCacheContext = createContext();

export function usePlayerCache() {
  return useContext(PlayerCacheContext);
}

export function PlayerCacheProvider({ children }) {
  const [playerCache, setPlayerCache] = useState(() => {
    // Load from localStorage on init
    try {
      const cached = localStorage.getItem('playerCache');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed.data || {};
        }
      }
    } catch (e) {
      console.error('Error loading player cache:', e);
    }
    return {};
  });

  // Save to localStorage whenever cache updates. Accepts new cache object or updater fn (prev => newCache).
  const updateCache = useCallback((newCacheOrUpdater) => {
    setPlayerCache((prev) => {
      const newCache = typeof newCacheOrUpdater === 'function' ? newCacheOrUpdater(prev) : newCacheOrUpdater;
      try {
        localStorage.setItem('playerCache', JSON.stringify({
          data: newCache,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.error('Error saving player cache:', e);
      }
      return newCache;
    });
  }, []);

  // Get player data from cache or fetch via Cloud Function
  const getPlayer = useCallback(async (playerId) => {
    if (!playerId) return null;

    if (playerCache[playerId]) return playerCache[playerId];

    try {
      const users = await readApi.getUsersByIds([playerId]);
      const cachedData = users[playerId];
      if (cachedData) {
        const full = { ...cachedData, photoURL: cachedData.photoURL ?? null };
        updateCache((prev) => ({ ...prev, [playerId]: full }));
        return full;
      }
    } catch (error) {
      console.error(`Error fetching player ${playerId}:`, error);
    }

    return null;
  }, [playerCache, updateCache]);

  // Batch fetch multiple players via Cloud Function
  const getPlayers = useCallback(async (playerIds) => {
    if (!playerIds || playerIds.length === 0) return {};

    const uniqueIds = [...new Set(playerIds.filter((id) => id))];
    const result = {};
    const missingIds = [];

    for (const playerId of uniqueIds) {
      if (playerCache[playerId]) {
        result[playerId] = playerCache[playerId];
      } else {
        missingIds.push(playerId);
      }
    }

    if (missingIds.length > 0) {
      try {
        const users = await readApi.getUsersByIds(missingIds);
        const newCache = { ...playerCache };
        for (const [id, data] of Object.entries(users)) {
          const full = { ...data, photoURL: data.photoURL ?? null };
          result[id] = full;
          newCache[id] = full;
        }
        updateCache(newCache);
      } catch (error) {
        console.error('Error batch fetching players:', error);
        for (const playerId of missingIds) {
          if (!result[playerId]) {
            const playerData = await getPlayer(playerId);
            if (playerData) result[playerId] = playerData;
          }
        }
      }
    }

    return result;
  }, [playerCache, getPlayer, updateCache]);

  // Get player display name (from cache if available), init caps and proper spaces
  const getPlayerName = useCallback((playerId) => {
    if (!playerId) return 'Unknown';
    const cached = playerCache[playerId];
    const raw = cached?.displayName || cached?.email || 'Unknown';
    return toInitCaps(raw) || 'Unknown';
  }, [playerCache]);

  // Get player photo URL (from cache if available)
  const getPlayerPhotoUrl = useCallback((playerId) => {
    if (!playerId) return null;
    const cached = playerCache[playerId];
    return cached?.photoURL || null;
  }, [playerCache]);

  // Group/apartment short code + member ID or flat (e.g. "TA-101" or "TCA-42") for display below player name
  const getPlayerApartmentFlat = useCallback((playerId) => {
    if (!playerId) return '';
    const cached = playerCache[playerId];
    const memberId = cached?.groupMemberId || cached?.flatNumber;
    if (!memberId) return '';
    const groupCode = (cached.groupShortCode || '').trim();
    const aptCode = (cached.apartmentShortCode || '').trim();
    if (groupCode) return `${groupCode}-${memberId}`;
    if (aptCode) return `${aptCode}-${memberId}`;
    return String(memberId);
  }, [playerCache]);

  // Clear cache
  const clearCache = useCallback(() => {
    setPlayerCache({});
    localStorage.removeItem('playerCache');
  }, []);

  const value = {
    playerCache,
    getPlayer,
    getPlayers,
    getPlayerName,
    getPlayerPhotoUrl,
    getPlayerApartmentFlat,
    clearCache
  };

  return (
    <PlayerCacheContext.Provider value={value}>
      {children}
    </PlayerCacheContext.Provider>
  );
}

